import { Box, Checkbox, Menu, MenuItem, TextField, Typography } from "@leap4/frontend";
import React, { useMemo, useState } from "react";
import { useGetExplorerValues } from "../../../../api/generated/api";

interface IProps {
    campaign: string;
    dimension: string;
    label: string;
    from: string;
    to: string;
    selected: string[];
    onChange: (values: string[]) => void;
}

/** Scope filter over one dimension's values. Options load only when the menu is first opened. */
const ValueSelect = ({
    campaign,
    dimension,
    label,
    from,
    to,
    selected,
    onChange,
}: IProps): JSX.Element => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [opened, setOpened] = useState(false);
    const [filter, setFilter] = useState("");

    const { data, isLoading } = useGetExplorerValues(
        { campaign, dimension, from, to },
        { query: { enabled: opened, staleTime: 600000, refetchOnWindowFocus: false } }
    );

    const options = useMemo(() => {
        const all = data ?? [];
        const q = filter.trim().toLowerCase();
        return q ? all.filter((v) => v.toLowerCase().includes(q)) : all;
    }, [data, filter]);

    const toggle = (v: string): void =>
        onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

    return (
        <>
            {/* The count renders inside the field; helper text would make this taller than its neighbours. */}
            <TextField
                select
                size="small"
                label={label}
                value=""
                SelectProps={{
                    open: false,
                    displayEmpty: true,
                    renderValue: () => (selected.length > 0 ? `${selected.length} selected` : ""),
                    onOpen: (e: React.SyntheticEvent) => {
                        setAnchor(e.currentTarget as HTMLElement);
                        setOpened(true);
                    },
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140 }}
            >
                <MenuItem value="" />
            </TextField>

            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                PaperProps={{ sx: { maxHeight: 360, width: 260 } }}
            >
                <Box sx={{ px: 1.5, py: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder={`Filter ${label.toLowerCase()}`}
                        value={filter}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
                    />
                </Box>
                {isLoading && (
                    <Typography variant="caption" sx={{ display: "block", px: 2, py: 1, color: "text.disabled" }}>
                        Loading…
                    </Typography>
                )}
                {!isLoading && options.length === 0 && (
                    <Typography variant="caption" sx={{ display: "block", px: 2, py: 1, color: "text.disabled" }}>
                        No values in this range.
                    </Typography>
                )}
                {options.map((v) => (
                    <MenuItem key={v} dense onClick={() => toggle(v)} sx={{ py: 0.25 }}>
                        <Checkbox size="small" checked={selected.includes(v)} sx={{ p: 0.5, mr: 1 }} />
                        <Box sx={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {v}
                        </Box>
                    </MenuItem>
                ))}
                {selected.length > 0 && (
                    <MenuItem dense onClick={() => onChange([])} sx={{ color: "text.secondary" }}>
                        Clear all
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default ValueSelect;
